import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import Papa from 'papaparse';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiDownload, FiPlus, FiSearch, FiAlertTriangle } = FiIcons;

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const MODE_LABEL = { all: 'All', part: 'Part number', model: 'Model number' };

// created_at is stored as "YYYY-MM-DD HH:MM:SS" in UTC
const when = (stamp) => {
  const d = new Date(`${String(stamp).replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'MMM d, yyyy');
};

const searchHref = (g) => {
  const mode = g.modes.length === 1 && g.modes[0] !== 'all' ? `&mode=${g.modes[0]}` : '';
  return `/?q=${encodeURIComponent(g.query)}${mode}`;
};

/**
 * What technicians searched for and did not find, most frequent first.
 * Each row can be re-run in the search (to see whether it is fixed) or turned into a new part.
 */
const MissedSearches = ({ onAddPart }) => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setReport(null);
    setError(null);
    try {
      setReport(await api('missedSearches', { days }));
    } catch (err) {
      setError(err.message);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const csv = Papa.unparse(report.groups.map((g) => ({
      Search: g.query, Times: g.count, People: g.people, Shops: g.shops, 'First seen': g.first_seen, 'Last seen': g.last_seen,
      'Searched by': g.modes.map((m) => MODE_LABEL[m] || m).join('; '),
    })));
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `missed-searches-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="panel text-left">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4 dark:border-slate-800">
        <div>
          <div className="caption mb-1">Missed searches</div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Searches that returned nothing, with no filters applied. Frequent ones are parts or numbers the catalog is missing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div role="group" aria-label="Time range" className="flex divide-x divide-slate-300 border border-slate-300 dark:divide-slate-700 dark:border-slate-700">
            {RANGES.map((r) => (
              <button key={r.days} type="button" aria-pressed={days === r.days} onClick={() => setDays(r.days)}
                className={`px-3 py-2 text-xs font-semibold ${days === r.days ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300'}`}>
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" className="btn-secondary !px-3 !py-2 text-xs" onClick={exportCsv} disabled={!report || report.groups.length === 0}>
            <SafeIcon icon={FiDownload} /> CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="m-4 flex items-start gap-2 border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300" role="alert">
          <SafeIcon icon={FiAlertTriangle} className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Could not load the report: {error}. Check that the <code className="font-mono">missed_searches</code> table exists.</span>
        </div>
      )}

      {!report && !error && (
        <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-500"><SafeIcon icon={FiLoader} className="animate-spin" /> Loading&hellip;</div>
      )}

      {report && report.groups.length === 0 && (
        <p className="px-4 py-16 text-center text-sm text-slate-600 dark:text-slate-400">
          No missed searches in the last {report.days} days. Searches that find nothing will appear here.
        </p>
      )}

      {report && report.groups.length > 0 && (
        <>
          <p className="px-4 pt-3 text-xs text-slate-500">
            {report.total.toLocaleString()} missed searches, {report.groups.length.toLocaleString()} distinct.
            {report.truncated && ' Showing the most recent 10,000.'}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-navy text-slate-300">
                <tr>
                  {['Search', 'Times', 'People', 'Last seen', ''].map((h) => (
                    <th key={h} className="px-4 py-2 font-cond text-[11px] font-semibold uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {report.groups.map((g) => (
                  <tr key={g.key} className="hover:bg-blue-50 dark:hover:bg-slate-800/60">
                    <td className="px-4 py-3">
                      <div className="font-mono font-semibold text-slate-900 dark:text-white">{g.query}</div>
                      {g.modes.some((m) => m !== 'all') && (
                        <div className="text-xs text-slate-500">{g.modes.map((m) => MODE_LABEL[m] || m).join(', ')} search</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono font-semibold">{g.count}</td>
                    <td className="px-4 py-3 font-mono">{g.people}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600 dark:text-slate-400">{when(g.last_seen)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Link to={searchHref(g)} className="btn-secondary !px-3 !py-1.5 text-xs"><SafeIcon icon={FiSearch} /> Search again</Link>
                      <button type="button" className="btn-primary ml-2 !px-3 !py-1.5 text-xs" onClick={() => onAddPart(g.query)}><SafeIcon icon={FiPlus} /> Add part</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default MissedSearches;
